/**
 * Backend error-payload shape after `parseApiError` normalises a ky exception.
 *
 * Most endpoints return Symfony violations (HTTP 422) or a flat `message`
 * (HTTP 400/500). `status` is filled when the raw `Response` object was
 * accessible (e.g. HTTP 413 plain-text bodies that `handleJsonResponse` can't parse).
 */
export interface ParsedApiError {
    violations: Array<{ field: string; message: string }> | null
    message: string | null
    status: number | null
}

/**
 * Normalise a ky exception into a `ParsedApiError`.
 *
 * `err.response` can come in two shapes:
 *   1. Raw `Response` object — `handleJsonResponse` failed to parse the body
 *      (e.g. HTTP 413 returning plain text). Has `.status` and `.json()`.
 *   2. Already-parsed JSON — `handleJsonResponse` succeeded with a structured
 *      body (e.g. HTTP 422 with `violations`). Has fields like `.violations` directly.
 *
 * Returns an empty `ParsedApiError` (`{ violations: null, message: null, status: null }`)
 * when `err.response` is missing — caller can fall back to a default message.
 */
export const parseApiError = async (err: any): Promise<ParsedApiError> => {
    const empty: ParsedApiError = { violations: null, message: null, status: null }
    if (!err?.response) return empty

    if (typeof err.response.json === 'function') {
        // Case 1: raw Response object.
        const status = typeof err.response.status === 'number' ? err.response.status : null
        try {
            const data = await err.response.json()
            return {
                violations: Array.isArray(data?.violations) ? data.violations : null,
                message: typeof data?.message === 'string' ? data.message : null,
                status,
            }
        } catch {
            return { violations: null, message: null, status }
        }
    }

    // Case 2: already-parsed JSON.
    const data = err.response
    return {
        violations: Array.isArray(data?.violations) ? data.violations : null,
        message: typeof data?.message === 'string' ? data.message : null,
        status: typeof data?.status === 'number' ? data.status : null,
    }
}

/**
 * Convenience: produce a human-readable error description from a ky exception,
 * falling back to `fallback` when nothing usable is in the response.
 *
 * Joins multiple `violations` messages with `\n`. Does NOT trigger any
 * field-level `formContext.setError` — that is the caller's job.
 *
 * Caller that needs i18n for status-specific messages (e.g. HTTP 413 for upload
 * size limits) should branch on `parseApiError(err).status` before falling back.
 */
export const parseApiErrorDescription = async (err: any, fallback: string): Promise<string> => {
    const parsed = await parseApiError(err)
    if (parsed.violations?.length) return parsed.violations.map(v => v.message).join('\n')
    if (parsed.message) return parsed.message
    return fallback
}
